'use client';

import { useEffect } from 'react';
import {
  InsforgeProviderCore,
  type InsforgeProviderProps,
  type InitialAuthState,
  useInsforge as useReactInsforge,
  NavigationProvider,
  StyleProvider,
  InsforgeUser,
} from '@insforge/react';
import { NavigationAdapter } from '../navigation';

/**
 * Props for InsforgeBrowserProvider
 */
export type InsforgeBrowserProviderProps = InsforgeProviderProps;

/**
 * Clean up sensitive auth parameters from URL
 * This is needed in Next.js because SDK's detectAuthCallback may not run
 * when the SDK client is created at module level (before hydration)
 */
function cleanAuthParamsFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const authParams = ['access_token', 'user_id', 'email', 'name', 'csrf_token', 'error'];
  const hasAuthParams = authParams.some((p) => params.has(p));

  if (hasAuthParams) {
    const url = new URL(window.location.href);
    authParams.forEach((p) => url.searchParams.delete(p));
    window.history.replaceState({}, document.title, url.toString());
  }
}

/**
 * Internal component that cleans up URL params after auth is loaded
 * Must be inside the provider to access useInsforge
 */
function AuthParamsCleaner() {
  const { isLoaded } = useReactInsforge();

  useEffect(() => {
    // Only clean up after SDK has finished loading auth state
    // This ensures URL params are processed by SDK first
    if (isLoaded) {
      cleanAuthParamsFromUrl();
    }
  }, [isLoaded]);

  return null;
}

// Sync to server-side cookie using Authorization header
async function syncToCookie(token: string, user: InsforgeUser): Promise<void> {
  try {
    const response = await fetch('/api/auth', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'sync-token',
        user: user,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[InsforgeBrowserProvider] sync-token failed:', errorText);
    }
  } catch (err) {
    console.error('[InsforgeBrowserProvider] Failed to sync token to cookie:', err);
  }
}

// Sync token to server-side cookie on sign in
async function handleSignIn(token: string, user: InsforgeUser): Promise<void> {
  await syncToCookie(token, user);
}

// Sync token to server-side cookie on refresh (page load/refresh)
async function handleRefresh(token: string, user: InsforgeUser): Promise<void> {
  await syncToCookie(token, user);
}

// Clear HTTP-only cookie on sign out
async function handleSignOut(): Promise<void> {
  try {
    await fetch('/api/auth', { method: 'DELETE' });
  } catch {
    // API route doesn't exist - ignore
  }
}

/**
 * Browser-side Insforge Provider for Next.js
 *
 * This provider manages authentication state on the client side and automatically
 * syncs tokens to HTTP-only cookies via `/api/auth` route for server-side access.
 *
 * **Key Features:**
 * - Uses YOUR SDK client instance (shared with database/storage operations)
 * - Automatic token sync to HTTP-only cookies
 * - SSR hydration support via `initialState`
 * - Works with Next.js App Router
 *
 * ---
 *
 * ## Setup Guide
 *
 * ### Step 1: Create your SDK client
 *
 * @example
 * ```tsx
 * // lib/insforge.ts
 * import { createClient } from '@insforge/sdk';
 *
 * export const insforge = createClient({
 *   baseUrl: process.env.NEXT_PUBLIC_INSFORGE_URL!,
 *   anonKey: process.env.NEXT_PUBLIC_INSFORGE_ANON_KEY!,
 * });
 * ```
 *
 * ### Step 2: Create your Providers component
 *
 * @example
 * ```tsx
 * // app/providers.tsx
 * 'use client';
 * import { InsforgeBrowserProvider, type InitialAuthState } from '@insforge/nextjs';
 * import { insforge } from '@/lib/insforge';
 *
 * export function Providers({
 *   children,
 * }: {
 *   children: React.ReactNode;
 * }) {
 *   return (
 *     <InsforgeBrowserProvider
 *       client={insforge}
 *       afterSignInUrl="/dashboard"
 *     >
 *       {children}
 *     </InsforgeBrowserProvider>
 *   );
 * }
 * ```
 *
 * ### Step 3: Use in your root layout
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * import { getAuthFromCookies } from '@insforge/nextjs';
 * import { Providers } from './providers';
 *
 * export default async function RootLayout({
 *   children,
 * }: {
 *   children: React.ReactNode;
 * }) {
 *   const initialState = await getAuthFromCookies();
 *
 *   return (
 *     <html>
 *       <body>
 *         <Providers initialState={initialState}>
 *           {children}
 *         </Providers>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * ### Step 4: Use auth components and hooks
 *
 * @example
 * ```tsx
 * // app/page.tsx
 * 'use client';
 * import { SignIn, UserButton, useInsforge } from '@insforge/nextjs';
 *
 * export default function Page() {
 *   const { isSignedIn, user } = useInsforge();
 *
 *   if (!isSignedIn) return <SignIn />;
 *
 *   return (
 *     <div>
 *       <UserButton />
 *       <p>Welcome {user?.email}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * ---
 *
 * ## Why pass your own client?
 *
 * Authentication tokens are stored in the SDK client instance's memory.
 * If auth components use a different client than your app, they won't share
 * tokens, causing authentication to fail for database/storage operations.
 *
 * By passing YOUR client to the provider, all operations use the same
 * authenticated session.
 */
export function InsforgeBrowserProvider({
  children,
  client,
  afterSignInUrl = '/',
  onAuthChange,
  initialState = { user: null, userId: null },
}: InsforgeBrowserProviderProps) {
  const providerProps: InsforgeProviderProps = {
    children,
    client,
    afterSignInUrl,
    onAuthChange,
    onSignIn: handleSignIn,
    onSignOut: handleSignOut,
    onRefresh: handleRefresh,
    initialState,
  };

  return (
    <StyleProvider>
      <NavigationProvider adapter={NavigationAdapter}>
        <InsforgeProviderCore {...providerProps}>
          {/* Clean up auth params from URL after SDK loads auth state */}
          <AuthParamsCleaner />
          {children}
        </InsforgeProviderCore>
      </NavigationProvider>
    </StyleProvider>
  );
}

/**
 * @deprecated Use `InsforgeBrowserProvider` instead.
 *
 * This alias is kept for backward compatibility and will be removed in a future version.
 *
 * Migration:
 * ```tsx
 * // Before
 * import { ClientInsforgeProvider } from '@insforge/nextjs';
 *
 * // After
 * import { InsforgeBrowserProvider } from '@insforge/nextjs';
 * ```
 */
export const ClientInsforgeProvider = InsforgeBrowserProvider;

/**
 * Hook to access Insforge context
 *
 * Re-exports the hook from @insforge/react for convenience.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isSignedIn, signOut } = useInsforge();
 *
 *   if (!isSignedIn) return <SignIn />;
 *
 *   return (
 *     <div>
 *       <p>Welcome {user.email}</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useInsforge = useReactInsforge;

// Re-export types
export type { InitialAuthState };
